"use client";

import { useState, useEffect, useCallback } from "react";
import { X, Copy, Trash2, Bug } from "lucide-react";

interface LogEntry {
  id: number;
  type: "log" | "error" | "warn" | "info";
  message: string;
  timestamp: Date;
}

let logId = 0;
const logListeners: ((entry: LogEntry) => void)[] = [];

// console 메서드 오버라이드
if (typeof window !== "undefined") {
  const originalConsole = {
    log: console.log.bind(console),
    error: console.error.bind(console),
    warn: console.warn.bind(console),
    info: console.info.bind(console),
  };

  const createLogger = (type: LogEntry["type"]) => {
    return (...args: unknown[]) => {
      originalConsole[type](...args);
      const message = args
        .map((arg) =>
          typeof arg === "object" ? JSON.stringify(arg, null, 2) : String(arg)
        )
        .join(" ");
      const entry: LogEntry = {
        id: ++logId,
        type,
        message,
        timestamp: new Date(),
      };
      logListeners.forEach((listener) => listener(entry));
    };
  };

  console.log = createLogger("log");
  console.error = createLogger("error");
  console.warn = createLogger("warn");
  console.info = createLogger("info");
}

export function DebugConsole() {
  const [isOpen, setIsOpen] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const listener = (entry: LogEntry) => {
      setLogs((prev) => [...prev.slice(-99), entry]); // 최대 100개 유지
    };
    logListeners.push(listener);
    return () => {
      const idx = logListeners.indexOf(listener);
      if (idx > -1) logListeners.splice(idx, 1);
    };
  }, []);

  const handleCopy = useCallback(async () => {
    const text = logs
      .map(
        (log) =>
          `[${log.timestamp.toLocaleTimeString()}] [${log.type.toUpperCase()}] ${log.message}`
      )
      .join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback for iOS
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [logs]);

  const handleClear = useCallback(() => {
    setLogs([]);
  }, []);

  const getTypeColor = (type: LogEntry["type"]) => {
    switch (type) {
      case "error":
        return "text-red-500 bg-red-50";
      case "warn":
        return "text-yellow-600 bg-yellow-50";
      case "info":
        return "text-blue-500 bg-blue-50";
      default:
        return "text-neutral-700 bg-white";
    }
  };

  // 개발 환경에서만 표시
  if (process.env.NODE_ENV === "production") {
    return null;
  }

  return (
    <>
      {/* 우측 가장자리 미니 탭 - 얇지만 터치 가능 */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={`fixed top-1/2 -translate-y-1/2 right-0 z-[9999] rounded-l transition-all duration-200 ${isOpen ? "translate-x-full opacity-0 pointer-events-none" : "translate-x-0 opacity-100"}`}
        style={{ touchAction: "manipulation" }}
        title="Debug Console"
      >
        {/* 시각적 표시: 얇은 바 */}
        <span className="block w-[4px] h-12 bg-neutral-400/40 rounded-l" />
        {/* 터치 영역: 실제 히트 영역은 넉넉하게 */}
        <span className="absolute inset-0 -left-6 -top-2 -bottom-2" />
        {logs.some((l) => l.type === "error") && (
          <span className="absolute top-0 -left-0.5 w-2 h-2 bg-red-500 rounded-full" />
        )}
      </button>

      {/* 배경 오버레이 */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[9999] bg-black/20 transition-opacity"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* 우측 Drawer */}
      <div
        className={`fixed top-0 right-0 z-[10000] h-full w-[360px] max-w-[85vw] bg-white dark:bg-neutral-900 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* 헤더 */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-700 shrink-0">
          <div className="flex items-center gap-2">
            <Bug className="w-4 h-4 text-neutral-500" />
            <h2 className="font-semibold text-sm text-neutral-800 dark:text-neutral-200">Debug Console</h2>
            {logs.length > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 bg-neutral-100 dark:bg-neutral-800 text-neutral-500 rounded-full font-mono">
                {logs.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleCopy}
              className="p-1.5 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-md transition-colors"
              style={{ touchAction: "manipulation" }}
              title={copied ? "복사됨!" : "로그 복사"}
            >
              <Copy className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="p-1.5 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-md transition-colors"
              style={{ touchAction: "manipulation" }}
              title="로그 지우기"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-1.5 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-md transition-colors"
              style={{ touchAction: "manipulation" }}
              title="닫기"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* 복사 완료 토스트 */}
        {copied && (
          <div className="mx-4 mt-2 px-3 py-1.5 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-md text-center shrink-0">
            클립보드에 복사됨
          </div>
        )}

        {/* 로그 목록 */}
        <div className="flex-1 overflow-auto p-2 space-y-0.5">
          {logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-neutral-400 gap-2">
              <Bug className="w-8 h-8 opacity-30" />
              <p className="text-sm">로그가 없습니다</p>
            </div>
          ) : (
            logs.map((log) => (
              <div
                key={log.id}
                className={`px-2.5 py-1.5 rounded text-[11px] font-mono break-all leading-relaxed ${getTypeColor(log.type)}`}
              >
                <span className="text-neutral-400 mr-1.5">
                  {log.timestamp.toLocaleTimeString()}
                </span>
                <span className="font-semibold mr-1.5">
                  [{log.type.toUpperCase()}]
                </span>
                <span className="whitespace-pre-wrap">{log.message}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
